"use client"

import { useMemo, useState, useCallback } from "react"
import Link from "next/link"
import { ArrowRight, ExternalLink, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ScholarshipCard } from "@/components/scholarship-card"
import type { ScholarshipOfferListItem } from "@/app/actions/scholarships"
import { cn } from "@/lib/utils"

type SortKey = "newest" | "name" | "intake"

const APPLY_URL = "https://apply.mbg.mn"

function toTime(v: ScholarshipOfferListItem["createdAt"]): number {
  if (v instanceof Date) return v.getTime()
  return new Date(v as string).getTime()
}

interface ScholarshipsDirectoryProps {
  offers: ScholarshipOfferListItem[]
}

export const ScholarshipsDirectory = ({ offers }: ScholarshipsDirectoryProps) => {
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState<SortKey>("newest")
  const [language, setLanguage] = useState<string>("all")
  const [intake, setIntake] = useState<string>("all")

  const languages = useMemo(() => {
    const s = new Set<string>()
    for (const o of offers) s.add(o.teachingLanguage)
    return Array.from(s).sort((a, b) => a.localeCompare(b))
  }, [offers])

  const intakes = useMemo(() => {
    const s = new Set<string>()
    for (const o of offers) s.add(o.intake)
    return Array.from(s).sort((a, b) => a.localeCompare(b))
  }, [offers])

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = offers.filter((o) => {
      if (language !== "all" && o.teachingLanguage !== language) return false
      if (intake !== "all" && o.intake !== intake) return false
      if (!q) return true
      const hay = [
        o.university,
        o.location ?? "",
        o.offerId,
        ...o.majors,
      ]
        .join(" ")
        .toLowerCase()
      return hay.includes(q)
    })

    list = [...list]
    if (sort === "newest") {
      list.sort((a, b) => toTime(b.createdAt) - toTime(a.createdAt))
    } else if (sort === "name") {
      list.sort((a, b) =>
        a.university.localeCompare(b.university, "en", { sensitivity: "base" })
      )
    } else {
      list.sort((a, b) =>
        a.intake.localeCompare(b.intake, "en", { sensitivity: "base" })
      )
    }
    return list
  }, [offers, search, sort, language, intake])

  const handleReset = useCallback(() => {
    setSearch("")
    setSort("newest")
    setLanguage("all")
    setIntake("all")
  }, [])

  const hasActiveFilters =
    search.trim() !== "" || language !== "all" || intake !== "all" || sort !== "newest"

  if (offers.length === 0) {
    return (
      <p className="text-center text-muted-foreground">
        Одоогоор тэтгэлэг бүртгэгдээгүй байна.
      </p>
    )
  }

  return (
    <div className="space-y-8">
      {/* <div className="rounded-2xl border border-primary/25 bg-primary/5 p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-foreground">
              Тэтгэлэгт өргөдөл илгээх
            </h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              Дэлгэрэнгүй тэтгэлэг сонгоод доорх жагсаалаас үзнэ үү. Өргөдлөө{" "}
              <span className="font-medium text-foreground">apply.mbg.mn</span>{" "}
              дээр илгээнэ үү.
            </p>
          </div>
          <Button
            asChild
            size="lg"
            className="shrink-0 gap-2 rounded-xl shadow-md"
          >
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center"
            >
              apply.mbg.mn руу очих
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
          </Button>
        </div>
      </div> */}

      <div className="space-y-4">
        <div className="relative">
          <Label htmlFor="scholarship-search" className="sr-only">
            Хайлт Search universities, location, majors
          </Label>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="scholarship-search"
            type="search"
            placeholder="Их сургууль, байршил, хөтөлбөр…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn("pl-10", "h-11")}
            aria-label="Хайлт: их сургууль, байршил, хөтөлбөр / Search by university, location, majors"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="sort-select">Эрэмбэлэх / Sort</Label>
            <Select
              value={sort}
              onValueChange={(v) => setSort(v as SortKey)}
            >
              <SelectTrigger id="sort-select" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Шинэ эхэндээ / Newest first</SelectItem>
                <SelectItem value="name">Их сургуулийн нэрээр A–Z</SelectItem>
                <SelectItem value="intake">Элсэлтийн хугацаагаар / Intake</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lang-select">Хэл / Language</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger id="lang-select" className="h-11">
                <SelectValue placeholder="Бүгд" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Бүгд / All</SelectItem>
                {languages.map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {lang}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="intake-select">Элсэлт / Intake</Label>
            <Select value={intake} onValueChange={setIntake}>
              <SelectTrigger id="intake-select" className="h-11">
                <SelectValue placeholder="Бүгд" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Бүгд / All</SelectItem>
                {intakes.map((i) => (
                  <SelectItem key={i} value={i}>
                    {i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full"
              onClick={handleReset}
              disabled={!hasActiveFilters}
            >
              Цэвэрлэх / Reset
            </Button>
          </div>
        </div>
      </div>

      {filteredSorted.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-muted/30 px-6 py-12 text-center">
          <p className="text-muted-foreground">
            Таны шүүлтүүрт тохирох тэтгэлэг олдсонгүй. Хайлт эсвэл шүүлтүүрээ
            өөрчилж үзнэ үү.
          </p>
          <Button variant="link" className="mt-2" onClick={handleReset}>
            Бүгдийг харуулах
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {filteredSorted.length} тэтгэлэг харагдаж байна
          </p>
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredSorted.map((offer) => (
              <li key={offer.id}>
                <ScholarshipCard
                  offer={{
                    id: offer.id,
                    offerId: offer.offerId,
                    university: offer.university,
                    location: offer.location,
                    teachingLanguage: offer.teachingLanguage,
                    intake: offer.intake,
                    imageUrl: offer.imageUrl,
                    majors: offer.majors,
                  }}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex justify-center border-t pt-10">
        <Button asChild size="lg" variant="default" className="gap-2 rounded-xl">
          <Link href={APPLY_URL} target="_blank" rel="noopener noreferrer">
            Тэтгэлэгт өргөдөл илгээх — apply.mbg.mn
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}
